import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getAccessToken, parseAccessTokenClaims, showToast, useAuth } from '@beatq/shared';
import { BoardModalShell } from '../order-board/BoardActionModals.js';
import { PIN_IDLE_RETURN_COPY, PIN_SIGN_IN_MODAL_DISMISSIBLE } from './pinPadLogic.js';
import { PinSignInForm } from './PinSignInForm.js';
import {
  listAuthStaff,
  pinEligibleStaff,
  staffListFromEnvelope,
  submitStaffPinSignIn,
} from './staffPinApi.js';
import { usePinIdleTimeout } from './usePinIdleTimeout.js';

export type PinSignInModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  title?: string;
};

export function PinSignInModal({
  open,
  onClose,
  onSuccess,
  title = 'PIN sign-in',
}: PinSignInModalProps) {
  const { authenticated, signInPath } = useAuth();
  const claims = parseAccessTokenClaims(getAccessToken());
  const [idleLocked, setIdleLocked] = useState(false);
  const [returnedToSelector, setReturnedToSelector] = useState(false);
  const [staffId, setStaffId] = useState('');
  const [pin, setPin] = useState('');
  const visible = open || idleLocked;

  const returnToSelector = useCallback(() => {
    setStaffId('');
    setPin('');
    setReturnedToSelector(true);
  }, []);

  usePinIdleTimeout(authenticated, () => {
    returnToSelector();
    setIdleLocked(true);
  });

  const query = useQuery({
    queryKey: ['merchant', 'auth', 'staff'],
    queryFn: () => listAuthStaff(),
    enabled: visible && authenticated && Boolean(claims.biz ?? claims.brn),
    staleTime: 5 * 60 * 1000,
    placeholderData: (previous) => previous,
  });
  const staff = pinEligibleStaff(staffListFromEnvelope(query.data));

  useEffect(() => {
    if (open) {
      returnToSelector();
    }
  }, [open, returnToSelector]);

  const signIn = useMutation({
    mutationFn: () => submitStaffPinSignIn({ staffId, pin }),
    onSuccess: () => {
      setIdleLocked(false);
      setReturnedToSelector(false);
      showToast('success', 'Signed in');
      onSuccess?.();
      onClose();
    },
  });

  if (!visible) return null;

  const missingBranch = !claims.brn;
  const errorMessage =
    staff.length > 0
      ? ((signIn.error as Error | undefined)?.message ?? null)
      : ((query.error as Error | undefined)?.message ??
        (signIn.error as Error | undefined)?.message ??
        null);

  return (
    <BoardModalShell
      title={title}
      screenId="SCR-MER-062"
      onClose={onClose}
      dismissible={PIN_SIGN_IN_MODAL_DISMISSIBLE}
    >
      {returnedToSelector ? (
        <p
          role="status"
          className="mer-detail-list-meta m-0 m10-pin-idle-status"
          data-testid="merchant-pin-return-to-selector"
        >
          {PIN_IDLE_RETURN_COPY}
        </p>
      ) : null}
      <PinSignInForm
        staff={staff}
        staffId={staffId}
        pin={pin}
        onStaffId={setStaffId}
        onPin={setPin}
        onSubmit={() => signIn.mutate()}
        pending={signIn.isPending}
        missingBranch={missingBranch}
        errorMessage={errorMessage}
        idPrefix="pinModal"
      />
      {staff.length === 0 ? (
        <p className="mer-detail-list-meta m-0">
          <Link to={signInPath} className="mer-detail-action">
            Merchant sign-in
          </Link>
        </p>
      ) : null}
    </BoardModalShell>
  );
}
